import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, MapPin, Phone, Star, Heart,
  ShieldCheck, Shield, Clock, Award, ArrowRight,
  Building2, Stethoscope, UserCheck, CalendarDays, Loader2
} from 'lucide-react';
import { facilityApi } from '../utils/api';
import { mockStates } from '../data/mock';

// SAMHSA's raw treatment-type list is often several near-duplicate variants of
// the same category (e.g. "Outpatient", "Regular outpatient treatment", and
// "Intensive outpatient treatment" all showing up together). Pick at most one
// representative per category so the badges shown are actually distinct.
const TREATMENT_CATEGORIES = [
  { key: 'detox', test: (t) => /detox/i.test(t) },
  { key: 'medication-assisted', test: (t) => /methadone|buprenorphine|naltrexone/i.test(t) },
  { key: 'residential-inpatient', test: (t) => /residential|inpatient/i.test(t) },
  { key: 'partial-hospitalization', test: (t) => /partial hospitalization/i.test(t) },
  { key: 'outpatient', test: (t) => /outpatient/i.test(t) },
];

// Logos (PNG/SVG, usually padded with white space) need to be shown in full via
// object-contain. Genuine photos (jpg/webp) look better filling the card via
// object-cover - contain would just shrink them and leave empty gradient visible
// whenever their aspect ratio doesn't match the card.
const isLikelyLogo = (photoUrl) => {
  try {
    const pathname = new URL(photoUrl).pathname.toLowerCase();
    return pathname.endsWith('.png') || pathname.endsWith('.svg');
  } catch {
    return false;
  }
};

const getDistinctTreatmentBadges = (treatmentTypes = [], max = 3) => {
  const seenCategories = new Set();
  const badges = [];

  for (const type of treatmentTypes) {
    const category = TREATMENT_CATEGORIES.find((c) => c.test(type))?.key || type;
    if (seenCategories.has(category)) continue;
    seenCategories.add(category);
    badges.push(type);
    if (badges.length >= max) break;
  }

  return badges;
};

const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [featuredCenters, setFeaturedCenters] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [featuredImageErrors, setFeaturedImageErrors] = useState(new Set());
  const [siteStats, setSiteStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await facilityApi.getFeatured();
        setFeaturedCenters(response.facilities || []);
      } catch (err) {
        console.error('Error fetching featured facilities:', err);
        setFeaturedCenters([]);
      } finally {
        setFeaturedLoading(false);
      }
    };

    const fetchStats = async () => {
      try {
        const response = await facilityApi.getStats();
        setSiteStats(response);
      } catch (err) {
        console.error('Error fetching site stats:', err);
      }
    };

    fetchFeatured();
    fetchStats();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Build query parameters
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.append('q', searchQuery.trim());
    }
    if (selectedState) {
      params.append('state', selectedState);
    }
    
    // Navigate to search page with parameters
    const queryString = params.toString();
    navigate(`/search${queryString ? `?${queryString}` : ''}`);
  };

  // Real counts from the facility database, not marketing placeholders -
  // shows nothing until the stats endpoint resolves rather than a wrong number.
  const stats = [
    {
      label: 'Treatment Centers',
      value: siteStats ? `${siteStats.total_facilities.toLocaleString()}+` : null,
      icon: Building2
    },
    {
      label: 'States Covered',
      value: siteStats ? Object.keys(siteStats.facilities_by_state).length : null,
      icon: MapPin
    },
    { label: '24/7 Support', value: 'Always', icon: Clock },
    { label: 'Free to Use', value: '100%', icon: ShieldCheck }
  ];

  const treatmentTypes = [
    {
      title: 'Inpatient Rehab',
      description: '24/7 medical supervision and comprehensive care in a residential setting.',
      icon: Building2,
      link: '/treatment-types#inpatient'
    },
    {
      title: 'Outpatient Programs',
      description: 'Flexible treatment that allows you to maintain work and family commitments.',
      icon: CalendarDays,
      link: '/treatment-types#outpatient'
    },
    {
      title: 'Detoxification',
      description: 'Medically supervised withdrawal process to safely remove substances.',
      icon: Stethoscope,
      link: '/treatment-types#detox'
    },
    {
      title: 'Dual Diagnosis',
      description: 'Specialized treatment for addiction and co-occurring mental health disorders.',
      icon: UserCheck,
      link: '/treatment-types#dual-diagnosis'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      location: 'California',
      text: 'This directory helped me find the perfect treatment center. The staff was compassionate and the program saved my life.',
      rating: 5
    },
    {
      name: 'Michael R.',
      location: 'Texas',
      text: 'I was skeptical at first, but the center I found through this site provided exactly what I needed. Two years sober now!',
      rating: 5
    },
    {
      name: 'Jennifer K.',
      location: 'Florida',
      text: 'The search tools made it easy to find a facility that accepted my insurance and specialized in my specific needs.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 text-white border-0 text-sm">
              🌟 SAMHSA-Verified Treatment Directory
            </Badge>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Find the Right 
              <span className="text-yellow-300 block">Addiction Treatment</span>
              Center Today
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Search thousands of verified treatment centers across the US. Get personalized recommendations 
              and connect with the care you deserve.
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="bg-white rounded-2xl p-6 shadow-2xl max-w-4xl mx-auto mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Search by treatment type, facility name, or condition..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 text-lg text-gray-900 placeholder:text-gray-500 border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                
                <div className="md:w-64">
                  <Select value={selectedState} onValueChange={setSelectedState}>
                    <SelectTrigger className="h-14 text-lg text-gray-900 border-2 border-gray-200">
                      <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockStates.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  type="submit" 
                  size="lg" 
                  className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg"
                >
                  Find Treatment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </form>

            {/* Crisis Help Banner */}
            <div className="bg-red-600 bg-opacity-90 rounded-xl p-6 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-4">
                <Phone className="h-8 w-8 text-white" />
                <div>
                  <p className="text-white font-semibold text-lg">Crisis? Get Help Now</p>
                  <a href="tel:1-888-968-1014">
                    <p className="text-yellow-300 text-xl font-bold hover:underline">
                      Call Now: 1-888-968-1014
                    </p>
                  </a>
                  <p className="text-red-100 text-sm">Free, confidential, 24/7 support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {stat.value !== null ? stat.value : (
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                  )}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Treatment Centers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Featured Treatment Centers
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Discover top-rated facilities with proven success rates and comprehensive care programs.
              </p>
            </div>

            {featuredLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : featuredCenters.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-8">
                {featuredCenters.map((center) => (
                  <Card key={center.id} className="overflow-hidden hover:shadow-lg transition-shadow border-0 shadow-md">
                    <div className="relative h-48 bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center overflow-hidden">
                      {center.photo_url && !featuredImageErrors.has(center.id) ? (
                        <img
                          src={center.photo_url}
                          alt={center.name}
                          className={
                            isLikelyLogo(center.photo_url)
                              ? "max-w-full max-h-full object-contain p-6"
                              : "absolute inset-0 w-full h-full object-cover"
                          }
                          onError={() => {
                            setFeaturedImageErrors((prev) => new Set(prev).add(center.id));
                          }}
                        />
                      ) : (
                        <Building2 className="h-16 w-16 text-white opacity-50" />
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-10 pointer-events-none"></div>
                      <Badge className="absolute top-4 left-4 bg-yellow-500 text-black border-0">
                        Featured
                      </Badge>
                    </div>

                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-semibold text-gray-900">{center.name}</h3>
                        {center.rating && (
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                            <span className="text-sm text-gray-600 ml-1">{center.rating}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center text-gray-600 mb-4">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span className="text-sm">{center.city}, {center.state}</span>
                        {center.reviews && (
                          <span className="text-sm ml-2">({center.reviews} reviews)</span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {getDistinctTreatmentBadges(center.treatmentTypes).map((specialty, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {specialty}
                          </Badge>
                        ))}
                      </div>

                      <Button asChild className="w-full">
                        <Link to={`/facility/${center.id}`}>
                          View Details
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null}

            <div className="text-center mt-8">
              <Button asChild variant="outline" size="lg">
                <Link to="/search">
                  View All Centers
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Types */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Types of Treatment Available
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Find the right level of care for your unique situation and recovery needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {treatmentTypes.map((type, index) => (
                <Card key={index} className="text-center border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <type.icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{type.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{type.description}</p>
                    <Button asChild variant="outline" size="sm">
                      <Link to={type.link}>Learn More</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Stories of Hope & Recovery
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Real people sharing their journey to recovery and the centers that helped them heal.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                    <div className="flex items-center">
                      <div className="bg-blue-100 rounded-full w-10 h-10 flex items-center justify-center mr-3">
                        <Heart className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.location}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
              Why Families Trust Addiction Rehab Call
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Verified Centers</h3>
                <p className="text-gray-600">All facilities are licensed, accredited, and continuously monitored for quality care standards.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">24/7 Support</h3>
                <p className="text-gray-600">Our helpline is available around the clock to provide immediate assistance and guidance.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Personalized Match</h3>
                <p className="text-gray-600">Our team helps match you with the right treatment program based on your specific needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Take the First Step Towards Recovery
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Every journey begins with a single step. Let us help you find the treatment center 
              that will support your path to lasting recovery.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-yellow-500 text-gray-900 hover:bg-yellow-400 font-semibold">
                <Link to="/search">
                  Find Treatment Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
                <Link to="/get-help">
                  Get Help & Support
                  <Phone className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <p className="text-blue-100 mt-6">
              Questions? Call our helpline: 
              <a href="tel:1-888-968-1014" className="text-yellow-300 font-semibold hover:underline ml-2">
                1-888-968-1014
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;